import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, EMPTY, Observable, combineLatest, concat, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, finalize, ignoreElements, map, mergeMap } from 'rxjs/operators';

import { AuthPayload, AuthState, AuthUser } from '../models/auth.model';
import { AuthError } from '../models/error.model';
import { AuthGateway } from '../usecases/auth.gateway';
import { AuthUsecase } from '../usecases/auth.usecase';
import { ProgressUsecase } from '../usecases/progress.usecase';

const FACTOR_ATTR = 'phone_number';

@Injectable()
export class AuthInteractor extends AuthUsecase {
  get authState$(): Observable<AuthState> {
    return this._authState.pipe(filter(({ status }) => status !== 'none'));
  }
  get token$(): Observable<string> {
    return this._authGateway.fetchAuthSession().pipe(map(session => session.tokens?.idToken?.toString() ?? ''));
  }
  get payload$(): Observable<AuthPayload> {
    return this._authGateway.fetchAuthSession().pipe(map(session => session.tokens?.idToken?.payload as AuthPayload));
  }

  private readonly _authState = new BehaviorSubject<AuthState>({ status: 'none' });

  constructor(
    private _progressUsecase: ProgressUsecase,
    private _authGateway: AuthGateway,
  ) {
    super();
    this.refreshCache();
  }

  signIn(username: string, password: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .signIn({ username, password })
      .pipe(
        mergeMap(step => {
          switch (step) {
            case 'DONE':
              return combineLatest([this.fetchAuthUser(), this._authGateway.fetchAuthSession(true)]).pipe(
                mergeMap(([user, session]) => {
                  const verified = user.preferredMFA === 'SMS' && user.attributes?.phone_number_verified;
                  const optional = session.tokens?.idToken?.payload['mfa'] === 'optional';
                  return of({ status: verified || optional ? 'signedIn' : 'requireMfa', user } as AuthState);
                }),
              );
            case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
              return of({ status: 'confirmSignIn', user: { attributes: { email: username } } } as AuthState);
            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
              return of({ status: 'requireNewPassword', user: { attributes: { email: username } } } as AuthState);
            default:
              return throwError(() => new Error('Invalid step'));
          }
        }),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: authState => this.setAuthState(authState),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  confirmSignIn(code: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .confirmSignIn({ challengeResponse: code })
      .pipe(
        mergeMap(() => this.fetchAuthUser()),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: user => this.setAuthState({ status: 'signedIn', user }),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  signOut(): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .signOut()
      .pipe(finalize(() => this._progressUsecase.dismiss(progressId)))
      .subscribe({
        next: () => this.setAuthState({ status: 'signIn' }),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  forgotPassword(username: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .resetPassword({ username })
      .pipe(finalize(() => this._progressUsecase.dismiss(progressId)))
      .subscribe({
        next: () => this.setAuthState({ status: 'resetPassword', user: { attributes: { email: username } } as AuthUser }),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  forgotPasswordSubmit(username: string, confirmationCode: string, newPassword: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .confirmResetPassword({ username, confirmationCode, newPassword })
      .pipe(finalize(() => this._progressUsecase.dismiss(progressId)))
      .subscribe({
        next: () => this.setAuthState({ status: 'signIn' }),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  completeNewPassword(password: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    const email = this._authState.value?.user?.attributes?.email;
    this._authGateway
      .confirmSignIn({ challengeResponse: password })
      .pipe(
        concatMap(() => this._authGateway.signOut()),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: () => this.setAuthState({ status: 'signIn', user: { attributes: { email } } as AuthUser }),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  changePassword(oldPassword: string, newPassword: string): Observable<never> {
    const { user } = this._authState.value;
    if (!user) {
      throw new Error('Not signed in.');
    }
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .updatePassword({ oldPassword, newPassword })
      .pipe(finalize(() => this._progressUsecase.dismiss(progressId)))
      .subscribe({
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  verifyAuthFactor(phoneNumber: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .fetchAuthSession(true)
      .pipe(
        mergeMap(() =>
          this.fetchAuthUser().pipe(
            mergeMap(user => {
              if (user?.preferredMFA === 'SMS' && user.attributes?.phone_number_verified) {
                this.setAuthState({ status: 'signedIn', user } as AuthState);
                return throwError(() => new AuthError('AlreadyEnabledException'));
              }
              return concat(
                this._authGateway.updateUserAttribute({ userAttributes: { [FACTOR_ATTR]: phoneNumber } }),
                this._authGateway.verifyUserAttribute({ userAttributeKey: FACTOR_ATTR }),
              );
            }),
          ),
        ),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  verifyAuthFactorSubmit(confirmationCode: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .fetchAuthSession(true)
      .pipe(
        mergeMap(() => {
          return this.fetchAuthUser().pipe(
            mergeMap(user => {
              if (user?.preferredMFA === 'SMS' && user.attributes?.phone_number_verified) {
                this.setAuthState({ status: 'signedIn', user } as AuthState);
                return throwError(() => new AuthError('AlreadyEnabledException'));
              }
              return concat(
                this._authGateway.verifyUserAttributeSubmit({ userAttributeKey: FACTOR_ATTR, confirmationCode }),
                this._authGateway.setPreferredMFA({ sms: 'PREFERRED' }),
                this._authGateway.fetchAuthSession(true).pipe(ignoreElements()),
                this.fetchAuthUser(),
              );
            }),
          );
        }),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: user => this.setAuthState({ status: 'signedIn', user } as AuthState),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  deleteAuthFactor(): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .fetchAuthSession(true)
      .pipe(
        mergeMap(() =>
          this.fetchAuthUser().pipe(
            mergeMap(authUser =>
              authUser?.preferredMFA !== 'SMS'
                ? EMPTY
                : concat(
                    this._authGateway.setPreferredMFA({ sms: 'DISABLED' }),
                    this._authGateway.deleteUserAttribute({ userAttributeKeys: [FACTOR_ATTR] }),
                    combineLatest([this.fetchAuthUser(), this._authGateway.fetchAuthSession(true)]),
                  ).pipe(
                    mergeMap(([user, session]) => {
                      const optional = session.tokens?.idToken?.payload['mfa'] === 'optional';
                      const authState = { status: optional ? 'signedIn' : 'requireMfa', user };
                      return of(authState as AuthState);
                    }),
                  ),
            ),
          ),
        ),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: authState => this.setAuthState(authState),
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  refreshCache(): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._progressUsecase.dismiss(progressId);
    this._authGateway
      .fetchAuthSession(true)
      .pipe(
        catchError(() => of(undefined)),
        mergeMap(session => {
          if (session?.tokens?.idToken) {
            return this.fetchAuthUser().pipe(
              catchError(() => of(undefined)),
              mergeMap(user => {
                if (user) {
                  const verified = user.preferredMFA === 'SMS' && user.attributes?.phone_number_verified;
                  const optional = session.tokens?.idToken?.payload['mfa'] === 'optional';
                  const authState = { status: verified || optional ? 'signedIn' : 'requireMfa', user };
                  return of(authState as AuthState);
                } else {
                  return of({ status: 'signIn' } as AuthState);
                }
              }),
            );
          } else {
            return of({ status: 'signIn' } as AuthState);
          }
        }),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        next: authState => this.setAuthState(authState),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  setAuthState(state: AuthState): void {
    this._authState.next(state);
  }

  private fetchAuthUser(): Observable<AuthUser> {
    return combineLatest([this._authGateway.fetchUserAttributes(), this._authGateway.fetchMFAPreference()]).pipe(
      map(
        ([attributes, mfa]) =>
          ({
            attributes: {
              sub: attributes?.sub || '',
              email: attributes?.email || '',
              ['phone_number']: attributes?.phone_number || '',
              ['phone_number_verified']: attributes?.phone_number_verified === 'true',
            },
            preferredMFA: mfa,
          }) as AuthUser,
      ),
    );
  }
}
